// src/features/records/services/ai.js

import { functions } from '../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { normalizeCallableError, requireUser } from './utils/auth';

const AI_PROMPT_MAX_LENGTH = 500;

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function extractSnapshotSections(rawSnapshot) {
  const snapshot = isPlainObject(rawSnapshot) ? rawSnapshot : {};

  const contentSource = isPlainObject(snapshot.content) ? snapshot.content : {};
  const title =
    typeof contentSource.title === 'string' ? contentSource.title : '';
  const body = typeof contentSource.body === 'string' ? contentSource.body : '';
  const tagsArray = Array.isArray(contentSource.tags) ? contentSource.tags : [];
  const tags = tagsArray
    .filter((tag) => typeof tag === 'string')
    .map((tag) => tag);

  const attributes = isPlainObject(snapshot.attributes)
    ? { ...snapshot.attributes }
    : {};

  const links = isPlainObject(snapshot.links) ? snapshot.links : {};
  const linksOutgoing = Array.isArray(links.outgoing)
    ? links.outgoing
        .filter((entry) => isPlainObject(entry))
        .map((entry) => ({ ...entry }))
    : [];

  return {
    content: {
      title,
      body,
      tags,
    },
    attributes,
    linksOutgoing,
  };
}

let processRecordInputFn = httpsCallable(functions, 'processRecordInput');

export function getProcessRecordInputFn() {
  return processRecordInputFn;
}

export function setProcessRecordInputFn(nextFn) {
  processRecordInputFn = nextFn;
}

/**
 * Request an AI proposal from the deployed Cloud Function.
 *
 * @param {Object} request
 * @param {string=} request.recordId
 * @param {string} request.prompt
 * @param {Object} request.currentSnapshot
 * @param {Object=} request.context
 * @returns {Promise<{ proposal: Object | null; message: string }>} 
 */
export async function processRecordInput(request = {}) {
  requireUser();

  const rawPrompt = typeof request?.prompt === 'string' ? request.prompt : '';
  const trimmedPrompt = rawPrompt.trim();
  if (!trimmedPrompt) {
    const error = new Error('Prompt is required');
    error.code = 'invalid-argument';
    throw error;
  }

  const truncatedPrompt = trimmedPrompt.slice(0, AI_PROMPT_MAX_LENGTH);
  const recordId =
    typeof request?.recordId === 'string' ? request.recordId.trim() : '';

  const {
    content: snapshotContent,
    attributes: snapshotAttributes,
    linksOutgoing,
  } = extractSnapshotSections(request?.currentSnapshot);

  const payload = {
    prompt: truncatedPrompt,
    currentSnapshot: {
      content: {
        title: snapshotContent.title,
        body: snapshotContent.body,
        tags: snapshotContent.tags,
      },
      attributes: snapshotAttributes,
      links: {
        outgoing: linksOutgoing,
      },
    },
  };

  if (recordId) {
    payload.recordId = recordId;
  }

  if (isPlainObject(request?.context)) {
    const contextPayload = {};
    if (
      typeof request.context.locale === 'string' &&
      request.context.locale.trim()
    ) {
      contextPayload.locale = request.context.locale.trim();
    }
    if (
      typeof request.context.timezone === 'string' &&
      request.context.timezone.trim()
    ) {
      contextPayload.timezone = request.context.timezone.trim();
    }
    if (Object.keys(contextPayload).length > 0) {
      payload.context = contextPayload;
    }
  }

  try {
    const response = await processRecordInputFn(payload);
    const data = response?.data;
    const proposal = isPlainObject(data?.proposal) ? data.proposal : null;
    const message =
      typeof data?.message === 'string' && data.message.trim()
        ? data.message
        : 'No meaningful edits detected.';

    return {
      proposal,
      message,
    };
  } catch (error) {
    throw normalizeCallableError(
      error,
      'An unexpected error occurred while requesting the AI proposal.'
    );
  }
}
